/* WH-172: bounded, retrying fetch for first-party storefront calls.
 *
 * Exposes window.EH.fetchRetry and window.EH.cartMutate. Loaded via a deferred
 * script tag in layout/theme.liquid placed before every consumer; call sites
 * still fall back to plain fetch (window.EH?.fetchRetry || fetch) so nothing
 * breaks if this file fails to load.
 *
 * Rules (see docs/WH-166/WH-172/implementation-plan.md):
 * - 10s AbortController timeout per attempt.
 * - Retries: network TypeError, HTTP 5xx, HTTP 429 (honours Retry-After).
 *   Backoff 1s/2s/4s with ±20% jitter, max 3 retries (4 attempts total).
 * - Never retries other 4xx, and never retries a caller-initiated abort —
 *   that AbortError is rethrown untouched.
 * - Cart mutations must go through cartMutate: an ambiguous failure (timeout /
 *   connection drop after send) is reconciled against GET /cart.js before any
 *   re-POST, so a retry can never double-apply an add or update.
 *
 * Also loadable from Node (>=18) for scripts/test-fetch-retry.js — keep this
 * file free of unconditional window/document references.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (root) {
    root.EH = root.EH || {};
    root.EH.fetchRetry = api.fetchRetry;
    root.EH.cartMutate = api.cartMutate;
  }
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";

  var DEFAULT_TIMEOUT_MS = 10000;
  var DEFAULT_RETRY_DELAYS = [1000, 2000, 4000];
  var RETRY_AFTER_CAP_MS = 30000;

  function jitter(ms) {
    return Math.round(ms * (0.8 + Math.random() * 0.4));
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function composeSignal(callerSignal, timeoutController) {
    if (!callerSignal) return timeoutController.signal;
    if (typeof AbortSignal !== "undefined" && AbortSignal.any) {
      return AbortSignal.any([callerSignal, timeoutController.signal]);
    }
    /* Older Safari: mirror the caller's abort onto the timeout controller */
    if (callerSignal.aborted) timeoutController.abort();
    else {
      callerSignal.addEventListener(
        "abort",
        function () {
          timeoutController.abort();
        },
        { once: true }
      );
    }
    return timeoutController.signal;
  }

  function isRetryableStatus(status) {
    return status >= 500 || status === 429;
  }

  function retryAfterMs(response) {
    var header =
      response.headers && response.headers.get
        ? response.headers.get("Retry-After")
        : null;
    if (header == null) return null;
    var secs = parseInt(header, 10);
    if (isNaN(secs) || secs < 0) return null;
    return Math.min(secs * 1000, RETRY_AFTER_CAP_MS);
  }

  /**
   * Drop-in fetch replacement.
   * fetchRetry(url, options, { timeoutMs, retryDelays }) -> Promise<Response>
   * Exhausted retries return the last (failing) Response for HTTP errors —
   * existing `if (!res.ok)` handling at call sites keeps working — and
   * rethrow the last error for network-level failures.
   */
  async function fetchRetry(url, options, config) {
    options = options || {};
    config = config || {};
    var timeoutMs = config.timeoutMs || DEFAULT_TIMEOUT_MS;
    var delays = config.retryDelays || DEFAULT_RETRY_DELAYS;
    var callerSignal = options.signal || null;

    for (var attempt = 0; ; attempt++) {
      var timeoutController = new AbortController();
      var timer = setTimeout(function () {
        timeoutController.abort();
      }, timeoutMs);
      try {
        var response = await fetch(
          url,
          Object.assign({}, options, {
            signal: composeSignal(callerSignal, timeoutController),
          })
        );
        if (!isRetryableStatus(response.status) || attempt >= delays.length) {
          return response;
        }
        var serverWait = retryAfterMs(response);
        await sleep(serverWait != null ? serverWait : jitter(delays[attempt]));
      } catch (err) {
        /* Deliberate cancellation by the caller: never retry, never rewrap */
        if (callerSignal && callerSignal.aborted) throw err;
        if (attempt >= delays.length) throw err;
        await sleep(jitter(delays[attempt]));
      } finally {
        clearTimeout(timer);
      }
    }
  }

  function requestedItems(payload) {
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && payload.id != null) return [payload];
    return [];
  }

  function variantQty(cart, variantId) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (
        String(items[i].variant_id) === String(variantId) ||
        String(items[i].id) === String(variantId)
      ) {
        total += items[i].quantity;
      }
    }
    return total;
  }

  /* Did an ambiguous /cart/add.js POST actually land? Every requested variant
   * must show at least its pre-mutation quantity + the requested amount. */
  function addApplied(payload, cartBefore, cartAfter) {
    var items = requestedItems(payload);
    if (!items.length) return false;
    for (var i = 0; i < items.length; i++) {
      var wanted = Number(items[i].quantity) || 1;
      if (
        variantQty(cartAfter, items[i].id) <
        variantQty(cartBefore, items[i].id) + wanted
      ) {
        return false;
      }
    }
    return true;
  }

  /* Did an ambiguous /cart/update.js POST land? Only `attributes` and `note`
   * updates are verifiable (that's all our call sites send). */
  function updateApplied(payload, cartAfter) {
    if (!payload) return false;
    if (payload.attributes) {
      var attrs = (cartAfter && cartAfter.attributes) || {};
      for (var key in payload.attributes) {
        if (String(attrs[key] || "") !== String(payload.attributes[key] || "")) {
          return false;
        }
      }
      return true;
    }
    if (payload.note != null) {
      return String((cartAfter && cartAfter.note) || "") === String(payload.note);
    }
    return false;
  }

  async function getCart(rootPath, delays) {
    var res = await fetchRetry(
      rootPath + "cart.js",
      {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      },
      { retryDelays: delays }
    );
    if (!res.ok) throw new Error("cart snapshot failed: " + res.status);
    return res.json();
  }

  function syntheticCartResponse(cart) {
    return new Response(JSON.stringify(cart), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  /**
   * Duplicate-safe cart mutation. url must be a /cart/add.js or /cart/update.js
   * path; payload is the JSON body. Returns a Response like fetch would —
   * on a reconciled ambiguous success the body is the current cart JSON
   * (call sites only rely on res.ok / refreshing the cart afterwards).
   */
  async function cartMutate(url, payload, config) {
    config = config || {};
    var timeoutMs = config.timeoutMs || DEFAULT_TIMEOUT_MS;
    var delays = config.retryDelays || DEFAULT_RETRY_DELAYS;
    /* Reconcile against the same origin the mutation targets; theme call
       sites pass relative paths, so the store root comes from Shopify.routes. */
    var rootPath = /^https?:\/\//.test(url)
      ? new URL(url).origin + "/"
      : (typeof window !== "undefined" &&
          window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
    var isAdd = url.indexOf("cart/add") !== -1;

    var cartBefore = null;
    try {
      cartBefore = await getCart(rootPath, delays);
    } catch (e) {
      /* No snapshot -> an ambiguous failure can't be reconciled; we then fail
       * closed (surface the error, no blind re-POST) to stay duplicate-safe. */
    }

    var lastError = null;
    for (var attempt = 0; ; attempt++) {
      try {
        var response = await fetchRetry(
          url,
          Object.assign(
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify(payload),
            },
            config.fetchOptions || {}
          ),
          { timeoutMs: timeoutMs, retryDelays: [] } /* single attempt; retries decided here */
        );
        /* 2xx / non-retryable 4xx -> hand back to the caller. A retryable
         * status is NOT proof the mutation didn't land: gateway statuses
         * (502/504) can be emitted after the origin committed the write, so
         * treat them as ambiguous too - reconcile before any re-POST. */
        if (!isRetryableStatus(response.status) || attempt >= delays.length) {
          return response;
        }
        if (!cartBefore) {
          return response; /* no baseline -> can't verify, fail closed */
        }
        try {
          var cartCheck = await getCart(rootPath, delays);
        } catch (e) {
          return response; /* can't verify -> fail closed, surface the 5xx */
        }
        var appliedNow = isAdd
          ? addApplied(payload, cartBefore, cartCheck)
          : updateApplied(payload, cartCheck);
        if (appliedNow) return syntheticCartResponse(cartCheck);
        cartBefore = cartCheck; /* keep the baseline current for the next check */
        var serverWait = retryAfterMs(response);
        await sleep(serverWait != null ? serverWait : jitter(delays[attempt]));
        lastError = null;
      } catch (err) {
        if (err && err.name === "AbortError" && config.fetchOptions && config.fetchOptions.signal && config.fetchOptions.signal.aborted) {
          throw err; /* caller cancelled */
        }
        /* Ambiguous: request may have reached Shopify. Reconcile before any retry. */
        lastError = err;
        if (cartBefore) {
          try {
            var cartNow = await getCart(rootPath, delays);
            var applied = isAdd
              ? addApplied(payload, cartBefore, cartNow)
              : updateApplied(payload, cartNow);
            if (applied) return syntheticCartResponse(cartNow);
            cartBefore = cartNow; /* keep the baseline current for the next check */
          } catch (e) {
            throw lastError; /* can't verify -> fail closed, no blind re-POST */
          }
        } else {
          throw lastError; /* never had a baseline -> fail closed */
        }
        if (attempt >= delays.length) throw lastError;
        await sleep(jitter(delays[attempt]));
      }
    }
  }

  return { fetchRetry: fetchRetry, cartMutate: cartMutate };
});
